import { FilterOperators } from "@crispico/foundation-gwt-js";
import { ColumnDefinition, EditMode, EntityEditorFormSimple, EntityEditorPage, EntityEditorPageProps, EntityTablePage, EntityTablePageReducers, EntityTablePageState, EntityTablePagePartialProps } from "@crispico/foundation-react";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { OverrideableElement, TabRouterPane } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { EntityDescriptor, FieldDescriptor } from "@crispico/foundation-react/entity_crud/EntityDescriptor";
import { FieldEditorProps, FieldRendererProps } from "@crispico/foundation-react/entity_crud/fieldRenderersEditors";
import StringFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/StringFieldRenderer";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { FormikProps } from "formik";
import _, { clone } from "lodash";
import { default as React, ReactNode } from "react";
import { Form, Checkbox, Grid, Segment, Button, Icon, SemanticICONS } from "semantic-ui-react";
import ColorFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/ColorFieldRenderer";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { GalleyFieldDescriptor } from "pages/Task/TaskFieldDescriptors";
import { RegistrationsFieldDescriptor } from "./FlightAndObjectActionFieldDescriptors";
import { ApplyFlightAndObjectActionListAction } from "./actions/ApplyFlightAndObjectActionListAction";

export const conditionFieldsKeys = ["airport", "airline", "number", "startTime", "endTime", "daysOfTheWeek", "origin", "destination", "departure", "planeType",
    "terminal", "schengen", "parkingIsContact", "flightType", "canceled", "sariaPort", "paxType", "parking", "busType", "degradedModeCondition", "unit", "registrations",
    "groundTimeLower", "groundTimeHigher", "groundAgent", "originGroup", "destinationGroup", "planeTypeGroup", "planeTypeList", "flightAndRotationSameUnit", "nightStop",
    "overlapEscaleGroup", "overlapEscale", "matriculationGroup", "withMatriculationGroup", "withRotation", "cycleDepartureOrder", "customObjectCode", "crewShuttle", "parkingGroup",
    "flightVersion", "isException", "priorityObjects", "priority", "lowerStaffNumber", "upperStaffNumber", "lowerPassengerNumber", "upperPassengerNumber"];
export const modificationFieldsKeys = ["showFlightInList", "showFlightInGantt", "oilGroup", "is3N", "flightOffset", "color", "invoiceAccount", "flightComment",
    "degradedModeModification", "flightUnit", "flightPriority", "blockObjectCreation", "flightSchengen"];
export const objcreationFieldsKeys = ["useObjectCreation", "taskType", "quantity", "objectName", "objectPriority", "objectOffset", "duration", "objectAlternateParking", "objectParking", "objectGalley",
    "objectApproachNumber", "objectMessageCreation", "vraIndex", "objectExceptionEvent", "objectFinishMissionEvent",
    "eventMonitoringRulesJson", "requiredEquipmentResourceQualificationType", "equipmentResourceFillPercentage", "emrjUseRotationFlightAsReference", "objectOffsetForRotationFlight", "objectComment",
];

const CLIENT_COLUMNS = ["conditions", "modification", "objectCreation"];
const FOA_TABLE_DEFAULT_ROW_HEIGHT = 105;
const FOA_TABLE_DEFAULT_CUSTOM_COLUMN_WIDTH = 300;
const DEFAULT_FILTER_APPLY: Filter = Filter.createComposed(FilterOperators.forComposedFilter.and, [Filter.create("date", FilterOperators.forDate.today, undefined)]);

export class FlightAndObjectActionEntityDescriptor extends EntityDescriptor {

    public getGraphQlFieldsToRequest(fields?: string[]) {
        let fieldsToRequest = super.getGraphQlFieldsToRequest([...new Set([...conditionFieldsKeys, ...modificationFieldsKeys, ...objcreationFieldsKeys,
            "usedModificationFields", ...Object.keys(this.fields).filter(key => key !== "parent" && key !== "children")])]);
        fieldsToRequest = fieldsToRequest.replace("usedModificationFields", "usedModificationFields children { " + fieldsToRequest + " } parent { " + fieldsToRequest + " }");
        return fieldsToRequest;
    }

    getDefaultColumnConfig() {
        let defaultColumnConfig = _.cloneDeep(super.getDefaultColumnConfig());
        defaultColumnConfig.configObject.columns?.map((column) => {
            if (CLIENT_COLUMNS.includes(column.name)) {
                column.width = FOA_TABLE_DEFAULT_CUSTOM_COLUMN_WIDTH;
            }
        });
        return defaultColumnConfig;
    }

    protected customize() {
        const that = this;
        this.icon = <Icon.Group>
            <Icon name='plane' />
            <Icon corner="bottom right" className="FlightAndObjectAction_settingIcon" name='setting' />
        </Icon.Group> as unknown as SemanticICONS
        this.defaultFilter = Filter.createForClient("parent", FilterOperators.forEntityManyToOne.isEmpty);

        this.addFieldDescriptor(new class extends FieldDescriptor {
            name = "conditions";
            clientOnly = true;

            getAppearsInUi(): boolean {
                return true;
            }

            protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
                let conditionFields: any[] = [];

                conditionFieldsKeys.forEach(key => {
                    if (entity[key]) {
                        conditionFields.push(<>
                            {_msg("FlightAndObjectAction." + key + ".label") + ":"}
                            {that.getField(key).renderField(entity, FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLabel: true }))}
                        </>);
                    }
                });

                return <div className="FlightAndObjectAction_customColumnContainer">{conditionFields}</div>;
            }
        }())
            .addFieldDescriptor(new class extends FieldDescriptor {
                name = "modification";
                clientOnly = true;

                getAppearsInUi(): boolean {
                    return true;
                }

                protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
                    let modificationFields: any[] = [];

                    modificationFieldsKeys.forEach(key => {
                        if (entity.usedModificationFields?.indexOf(key) > -1) {
                            modificationFields.push(<>
                                {_msg("FlightAndObjectAction." + key + ".label") + ":"}
                                {that.getField(key).renderField(entity, FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLabel: true }))}
                            </>);
                        }
                    });

                    return <div className="FlightAndObjectAction_customColumnContainer">{modificationFields}</div>;
                }
            }())
            .addFieldDescriptor(new class extends FieldDescriptor {
                name = "objectCreation";
                clientOnly = true;

                getAppearsInUi(): boolean {
                    return true;
                }

                protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
                    let objCreationFields: any[] = [];
                    let objCreation = <></>;

                    for (let i = 0; i < entity.children.length + 1; i++) {
                        let child = i === 0 ? entity : entity.children[i - 1];
                        objCreationFields = [];
                        objcreationFieldsKeys.forEach(key => {
                            // Exclude `useObjectCreation` (Activate) from rendering
                            if (key !== "useObjectCreation" && (child.useObjectCreation && child[key])) {
                                objCreationFields.push(<>
                                    {_msg("FlightAndObjectAction." + key + ".label") + ":"}
                                    {that.getField(key).renderField(child, FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLabel: true }))}
                                </>);
                            }
                        });
                        objCreation = <>
                            {objCreation}
                            <Segment className="FlightAndObjectAction_objectCreationContainer less-padding">
                                {objCreationFields}
                            </Segment>
                        </>;
                    }
                    return objCreation;
                }
            }())
            .addFieldDescriptor({ name: "color", type: FieldType.color, colorType: "number", additionalFieldRendererProps: FieldDescriptor.castAdditionalFieldRendererProps(ColorFieldRenderer, { style: { width: "auto", display: "inline-block" } }) })
            .addFieldDescriptor({ name: "departure", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "flightType", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 'S', label: _msg("FlightAndObjectAction.flightType.short.label") }, { from: 'M', label: _msg("FlightAndObjectAction.flightType.medium.label") }, { from: 'L', label: _msg("FlightAndObjectAction.flightType.long.label") }] } })
            .addFieldDescriptor({ name: "schengen", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("FlightAndObjectAction.schengen.domestic.label") }, { from: 2, label: _msg("FlightAndObjectAction.schengen.international.label") }] } })
            .addFieldDescriptor({ name: "parkingIsContact", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "canceled", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "registrations", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } }, new RegistrationsFieldDescriptor())
            .addFieldDescriptor({ name: "flightAndRotationSameUnit", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "nightStop", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "withMatriculationGroup", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "withRotation", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "crewShuttle", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("global.button.ignore") }, { from: 1, label: _msg("global.button.yes") }, { from: 2, label: _msg("global.button.no") }] } })
            .addFieldDescriptor({ name: "showFlightInGantt", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("flight.defaultShowFlightInGantt.leaveUnset") }, { from: 1, label: _msg("flight.defaultShowFlightInGantt.doNotShow") }, { from: 2, label: _msg("flight.defaultShowFlightInGantt.showInGantt") }] } })
            .addFieldDescriptor({ name: "showFlightInList", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: 0, label: _msg("flight.defaultShowFlightInGantt.leaveUnset") }, { from: 1, label: _msg("flight.defaultShowFlightInGantt.doNotShow") }, { from: 2, label: _msg("flight.defaultShowFlightInGantt.showInGantt") }] } })
            .addFieldDescriptor({ name: "paxType", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: "ALL", label: _msg("Flight.passangers.all.label") }, { from: "HC", label: _msg("Flight.passangers.hc.label") }, { from: "ECO", label: _msg("Flight.passangers.eco.label") }] } })
            .addFieldDescriptor({ name: "busType", type: FieldType.dropdown, fieldDescriptorSettings: { fieldIntervals: [{ from: "", label: _msg("FlightAndObjectAction.busType.ignore.label") }, { from: "any", label: _msg("FlightAndObjectAction.busType.any.label") }, { from: "none", label: _msg("FlightAndObjectAction.busType.doesNotExists.label") }, { from: "S", label: _msg("FlightAndObjectAction.busType.s.label") }, { from: "C", label: _msg("FlightAndObjectAction.busType.c.label") }, { from: "N", label: _msg("FlightAndObjectAction.busType.n.label") }, { from: "O", label: _msg("FlightAndObjectAction.busType.o.label") }] } })
            .addFieldDescriptor({ name: "startTime", type: FieldType.date })
            .addFieldDescriptor({ name: "endTime", type: FieldType.date })
            .addFieldDescriptor({ name: "objectGalley" }, new GalleyFieldDescriptor())
            .addFieldDescriptor({ name: "daysOfTheWeek", type: FieldType.daysOfTheWeek })
            .isInDefaultColumnConfig(true, "id", "name", "enabled", "conditions", "modification", "objectCreation");

        this.doForFields(CLIENT_COLUMNS, fd => fd.filterable = false);
        this.doForFields(CLIENT_COLUMNS, fd => fd.sortable = false);

        this.infoEditor.wrappedComponentClass = class extends EntityEditorPage<EntityEditorPageProps> {

            entityEditorForm = React.createRef<EntityEditorFormSimple>();
            modificationsEditorForm = React.createRef<FlightAndObjectActionModificationsEntityEditorFormSimple>();
            objectCreationEditorForm = React.createRef<FlightAndObjectActionObjectCreationEntityEditorFormSimple>();

            protected onTabChanged(ref: React.RefObject<any>) {
                if (ref.current) {
                    this.props.dispatchers.setInReduxState({
                        entity: { ...this.props.entity, ...ref.current.formik.current.values }
                    });
                }
            }

            protected getPropsForFormSimple() {
                let defaultProps = super.getPropsForFormSimple();
                let columns: ColumnDefinition[] = [];
                let columnsVisibleMap: { [field: string]: boolean } = {}
                defaultProps.columns?.forEach(column => {
                    if (!CLIENT_COLUMNS.includes(column.name)) {
                        columns.push(column);
                        columnsVisibleMap[column.name] = true;
                    }
                });
                defaultProps = { ...defaultProps, columns, columnsVisibleMap }
                return defaultProps;
            }

            protected getSpecificTabPanes(): (TabRouterPane | null)[] {
                const props = this.props;

                let conditionDescriptor: EntityDescriptor = clone(props.dispatchers.getSlice().entityDescriptor);
                let conditionFields: any = {};
                Object.keys(conditionDescriptor.fields).forEach(key => {
                    if (conditionFieldsKeys.includes(key)) {
                        conditionFields[key] = conditionDescriptor.fields[key];
                        conditionFields[key].enabled = true;
                    }
                });
                conditionDescriptor.fields = conditionFields;

                let modificationDescriptor: EntityDescriptor = clone(props.dispatchers.getSlice().entityDescriptor);
                let modificationFields: any = {};
                Object.keys(modificationDescriptor.fields).forEach(key => {
                    if (modificationFieldsKeys.includes(key)) {
                        modificationFields[key] = modificationDescriptor.fields[key];
                    }
                });
                modificationDescriptor.fields = modificationFields;

                let objcreationDescriptor: EntityDescriptor = clone(props.dispatchers.getSlice().entityDescriptor);
                let objcreationFields: any = {};
                Object.keys(objcreationDescriptor.fields).forEach(key => {
                    if (objcreationFieldsKeys.includes(key)) {
                        objcreationFields[key] = objcreationDescriptor.fields[key];
                    }
                });
                objcreationDescriptor.fields = objcreationFields;

                return [
                    {
                        routeProps: { path: "/conditions" },
                        menuItemProps: { content: _msg("FlightAndObjectAction.conditions.label") },
                        render: () => {
                            return this.props.entity ? <div className="ui fluid container EntityEditorPage_container">
                                <div className="ui segment EntityEditorPage_segment">
                                    <EntityEditorFormSimple
                                        ref={this.entityEditorForm} entity={props.entity}
                                        entityDescriptor={conditionDescriptor}
                                        onSubmitHandler={(values) => {
                                            props.dispatchers.setInReduxState({ entity: values });
                                            this.onSave();
                                        }} />
                                </div>
                            </div> : null
                        },
                        commit: () => this.onTabChanged(this.entityEditorForm)
                    },
                    {
                        routeProps: { path: "/flightModifications" },
                        menuItemProps: { content: _msg("FlightAndObjectAction.modification.title") },
                        render: () => {
                            return this.props.entity ? <div className="ui fluid container EntityEditorPage_container">
                                <div className="ui segment EntityEditorPage_segment">
                                    <FlightAndObjectActionModificationsEntityEditorFormSimple
                                        ref={this.modificationsEditorForm} entity={props.entity}
                                        entityDescriptor={modificationDescriptor}
                                        onSubmitHandler={(values) => {
                                            props.dispatchers.setInReduxState({ entity: values });
                                            this.onSave();
                                        }} />
                                </div>
                            </div>: null
                        },
                        commit: () => this.onTabChanged(this.modificationsEditorForm)
                    },
                    {
                        routeProps: { path: "/objectCreation" },
                        menuItemProps: { content: _msg("FlightAndObjectAction.object.creation.title") },
                        render: () => {
                            return this.props.entity ? <div className="ui fluid container EntityEditorPage_container">
                                <div className="ui segment EntityEditorPage_segment">
                                    <FlightAndObjectActionObjectCreationEntityEditorFormSimple
                                        ref={this.objectCreationEditorForm} entity={props.entity}
                                        entityDescriptor={objcreationDescriptor}
                                        onSubmitHandler={(values) => {
                                            props.dispatchers.setInReduxState({ entity: values });
                                            this.onSave();
                                        }} />
                                </div>
                            </div>: null
                        },
                        commit: () => this.onTabChanged(this.objectCreationEditorForm)
                    }
                ];
            }

            protected getExtraTabPanes(): (TabRouterPane | null)[] {
                let result: (TabRouterPane | null)[] = super.getExtraTabPanes();
                // change tab orders
                result = result.concat(this.getSpecificTabPanes());

                let flightAndObjectActionTab = this.createOneToManyTabPane("FlightAndObjectAction", "parent", { filter: Filter.createComposedForClient(FilterOperators.forComposedFilter.and, []) });
                flightAndObjectActionTab.menuItemProps.content = _msg("FlightAndObjectAction.multiple.object.creation.title");

                result.push(flightAndObjectActionTab);
                return result;
            }

            protected getExtraTabPanesInternal() {
                if (this.props.mode === EditMode.ADD) {
                    return this.getSpecificTabPanes();
                } else {
                    return super.getExtraTabPanesInternal();
                }
            }
        }

    }

    renderTable() {
        return <FlightAndObjectActionTablePageRRC {...super.renderTable().props} ref={this.entityTablePage} />;
    }

}

type FlightTablePageProps = EntityTablePagePartialProps & RRCProps<EntityTablePageState, EntityTablePageReducers>;

class FlightAndObjectActionTablePage extends EntityTablePage<FlightTablePageProps> {
    
    applyFlightAndObjectActionListActionRef = React.createRef<ApplyFlightAndObjectActionListAction>();

    protected getTableProps() {
        return { rowHeight: FOA_TABLE_DEFAULT_ROW_HEIGHT };
    }

    protected preRenderButtons(params: any): Array<OverrideableElement> {
        let buttons = super.preRenderButtons(params);
        if (!this.props.oneToManyMode) {
            buttons.push({
                element: <Button key="FlightAndObjectAction_applyButton" content={_msg("FlightAndObjectAction.apply.label")}
                    positive onClick={() => this.applyFlightAndObjectActionListActionRef.current?.openModal([])} />
            });
        }
        return buttons;
    };

    renderMain() {
        return <>
            {super.renderMain()}
            <ApplyFlightAndObjectActionListAction ref={this.applyFlightAndObjectActionListActionRef} initialFlightFilter={DEFAULT_FILTER_APPLY} foaFilter={this.getCustomQueryDefinitionForLoad().filter} 
                tablePage={this} getEntityUid={() => ""} getEntityAt={() => ({ id: null })} />
        </>
    }

}

const FlightAndObjectActionTablePageRRC = ReduxReusableComponents.connectRRC(EntityTablePageState, EntityTablePageReducers, FlightAndObjectActionTablePage);

class FlightAndObjectActionModificationsEntityEditorFormSimple extends EntityEditorFormSimple {

    renderFieldEditor(field: string, formikProps: FormikProps<any>) {
        let usedModificationFields: string = formikProps.values?.usedModificationFields || '';
        const { entityDescriptor } = this.props;
        const fieldDescriptor = entityDescriptor.getField(field)
        let fieldClassName = usedModificationFields.indexOf(field) > -1 ? "" : " disabledItem";

        return <Grid.Column key={field} className="EntityEditorPage_grid_row_column">
            <Form.Field key={fieldDescriptor.getFieldName()} disabled={!this.isFieldEnabled(fieldDescriptor)} data-cy={"field" + field}>
                <label className={fieldClassName}>{fieldDescriptor.getIcon()}{fieldDescriptor.getLabel(true)}</label>
                <div className="flex-container-row">
                    <Grid.Column className={"FlightAndObjectAction_modificationEditor" + fieldClassName}>
                        {fieldDescriptor.renderFieldEditor(formikProps, ({ autoFocus: this.props.autoFocusOnField ? field === this.props.autoFocusOnField: undefined }) as FieldEditorProps)}
                    </Grid.Column>
                    <Grid.Column className="FlightAndObjectAction_modificationEnable">
                        {React.createElement(Checkbox, { onChange: function () { formikProps.setFieldValue("usedModificationFields", usedModificationFields.indexOf(field) > -1 ? usedModificationFields.replace(field + ";", "") : (usedModificationFields + field + ";")) }, checked: usedModificationFields.indexOf(field) > -1 ? true : false })}
                    </Grid.Column>
                </div>
            </Form.Field>
        </Grid.Column>
    }

    renderHeaderEditor(formikProps: FormikProps<any>) {
        return <Grid.Row key="header" className="EntityEditorPage_grid_row">
            <Grid.Column className="EntityEditorPage_grid_header_column"></Grid.Column>
            <Grid.Column width={3} textAlign="center" className="EntityEditorPage_grid_header_column"><Form.Field label={"Enable"} /></Grid.Column>
        </Grid.Row>
    }

}

class FlightAndObjectActionObjectCreationEntityEditorFormSimple extends EntityEditorFormSimple {

    renderFieldEditor(field: string, formikProps: FormikProps<any>): ReactNode {
        if (field === "useObjectCreation") {
            return <></>
        }
        return <div className={"flex-grow-shrink-no-overflow" + (formikProps.values["useObjectCreation"] ? "" : " disabledItem")}>{super.renderFieldEditor(field, formikProps)}</div>;
    }

    renderHeaderEditor(formikProps: FormikProps<any>) {
        return <Grid.Row key="header" className="EntityEditorPage_grid_row FlightAndObjectAction_useObjectCreation">
            {super.renderFieldEditor("useObjectCreation", formikProps)}
        </Grid.Row>
    }

}
